import React, { useState, useRef } from "react";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import Tooltip from "./Tooltip";
import { commonStyles } from "../commonStyles";

type FileUploadProps = {
  id: string;
  label: string;
  tooltipText?: string;
  acceptedFileTypes?: string;
  maxFileSize?: number;
  onChange: (file: File | null) => void;
  value?: string | null;
  required?: boolean;
  isLogo?: boolean;
  previewSize?: "small" | "medium" | "large";
  helpText?: string;
};

const FileUpload = ({
  id,
  label,
  tooltipText,
  acceptedFileTypes = "image/*",
  maxFileSize = 5242880, // 5MB default
  onChange,
  value,
  required = false,
  isLogo = false,
  previewSize = "medium",
  helpText,
}: FileUploadProps) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(value || null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;

    if (!file) {
      return;
    }

    // Validate file size
    if (file.size > maxFileSize) {
      setError(`File size exceeds ${maxFileSize / 1048576}MB limit.`);
      return;
    }

    // Validate file type
    const fileTypeRegex = new RegExp(acceptedFileTypes.replace("*", ".*"));
    if (!fileTypeRegex.test(file.type)) {
      setError(
        `Invalid file type. Accepted types: ${acceptedFileTypes.replace(
          "*",
          ""
        )}`
      );
      return;
    }

    setError(null);

    // Create preview URL
    const reader = new FileReader();
    reader.onload = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);

    onChange(file);
  };

  const handleDelete = () => {
    setPreviewUrl(null);
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleChangeClick = () => {
    fileInputRef.current?.click();
  };

  const getSizeStyles = () => {
    if (isLogo) {
      return {
        width: "120px",
        height: "120px",
      };
    }

    switch (previewSize) {
      case "small":
        return { width: "100px", height: "100px" };
      case "large":
        return { width: "240px", height: "135px" };
      case "medium":
      default:
        return { width: "180px", height: "100px" };
    }
  };

  return (
    <div className="mb-6">
      <div className="flex items-center mb-1">
        <label htmlFor={id} className="block text-sm font-medium text-gray-900">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        {tooltipText && <Tooltip content={tooltipText} />}
      </div>

      {previewUrl ? (
        <div className="mb-2 relative" style={getSizeStyles()}>
          <img
            src={previewUrl}
            alt={`${label} preview`}
            className="object-contain w-full h-full border rounded-md bg-gray-50"
            style={{ maxWidth: "100%", maxHeight: "100%" }}
          />
          <div className="absolute right-0 bottom-0 flex">
            <button
              type="button"
              onClick={handleChangeClick}
              aria-label="Change file"
              className="bg-white text-gray-700 p-1 rounded-full shadow-md m-1"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && handleChangeClick()}
            >
              <EditIcon fontSize="small" />
            </button>
            <button
              type="button"
              onClick={handleDelete}
              aria-label="Delete file"
              className="bg-white text-gray-700 p-1 rounded-full shadow-md m-1"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && handleDelete()}
            >
              <DeleteIcon fontSize="small" />
            </button>
          </div>
        </div>
      ) : (
        <div
          className="border-2 border-dashed border-gray-300 rounded-md bg-gray-50 cursor-pointer flex flex-col items-center justify-center transition-all hover:border-gray-400"
          style={getSizeStyles()}
          onClick={handleChangeClick}
          onKeyDown={(e) => e.key === "Enter" && handleChangeClick()}
          tabIndex={0}
          role="button"
          aria-label={`Upload ${label}`}
        >
          <CloudUploadIcon
            sx={{
              color: "#9CA3AF",
              fontSize: commonStyles.fontSizes.xlarge,
              mb: 1,
            }}
          />
          <span className="text-sm text-gray-500">Click to upload</span>
          {helpText && (
            <span className="text-xs text-gray-400 mt-1">{helpText}</span>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        id={id}
        accept={acceptedFileTypes}
        onChange={handleFileChange}
        className="hidden"
      />

      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
};

export default FileUpload;
